import { useContext, useMemo, useState } from "react";

// packages
import { useTranslation } from "react-i18next";
import classNames from "classnames";

import styles from "./style.module.scss";

// provider and contexr
import { ContractContext } from "../../../providers/contract";

// types
import {
  CashOutAmount,
  ContractClassification,
  ESenderRole,
  IContractDetailsResponseData,
} from "../../../setup/api/services/contract/models";

// assets
import { ReactComponent as InfoIcon } from "../../../assets/images/svg/infoIcon.svg";

// components
import { ECONTRACT_STATUS } from "../../../enums";
import WaffyBankInfoModal from "./WaffyBankInfoModal";
import TooltipWrapper from "../../../components/common/TooltipWrapper/TooltipWrapper";

import { useSearchParams } from "react-router-dom";
import { AuthConfig } from "../../../setup/context/config/authConfig";

interface Props {
  contract?: IContractDetailsResponseData;
}

const Status = ({ contract }: Props): JSX.Element => {
  const [searchParams] = useSearchParams();
  const [showBankInfo, setShowBankInfo] = useState(false);
  const isMilestone = searchParams?.get("isMilestone") === "true";
  const client_id = searchParams?.get("client_id");
  const userId = AuthConfig.getUserInfo()?.id;
  const userIdentification = AuthConfig.getUserInfo()?.userIdentification;

  const { pausedState, actorType } = useContext(ContractContext);
  const { t } = useTranslation();

  const isAddressAdded = useMemo(() => {
    const { address, metaData } = contract || {};
    const requiresShippingInfo = metaData?.sellerRequiresShippingInfo;
    return requiresShippingInfo && address;
  }, [contract]);

  const statusConfig = useMemo(() => {
    const isContractWithoutReceiver =
      contract?.senderRole === ESenderRole.CUSTOMER && !contract?.receiverId;

    return {
      [ECONTRACT_STATUS.CREATED]: contract?.sourceContractId
        ? {
            img: "/assets/images/svg/cashOutApproved_status.svg",
            title: t("lbl_adding_amount_from_source_contract"),
            description: t("lbl_adding_amount_from_source_contract"),
          }
        : {
            img:
              contract?.contractClassification ===
                ContractClassification.BASIC ||
              contract?.contractClassification ===
                ContractClassification.BASIC_HARAJ
                ? "/assets/images/svg/waiting_payment_status.svg"
                : actorType === ESenderRole.PROVIDER
                ? "/assets/images/svg/stock_active_status.svg"
                : "/assets/images/svg/waiting_payment_status.svg",
            title:
              contract?.contractClassification ===
                ContractClassification.BASIC ||
              contract?.contractClassification ===
                ContractClassification.BASIC_HARAJ
                ? t("global_status_payment_title")
                : actorType === ESenderRole.PROVIDER
                ? t("global_status_stock_title")
                : t("global_status_payment_title"),

            description:
              contract?.contractClassification ===
                ContractClassification.BASIC ||
              contract?.contractClassification ===
                ContractClassification.BASIC_HARAJ
                ? actorType === ESenderRole.PROVIDER
                  ? t("lbl_waiting_for_payment_description_seller")
                  : t("status_payment_ubuyer_subtitle")
                : actorType === ESenderRole.PROVIDER
                ? t("status_stock_active_subtitle")
                : t("status_payment_ubuyer_subtitle"),
          },
      [ECONTRACT_STATUS.PARTIALLY_PAID]: {
        img:
          contract?.contractClassification === ContractClassification.BASIC ||
          contract?.contractClassification ===
            ContractClassification.BASIC_HARAJ
            ? "/assets/images/svg/waiting_payment_status.svg"
            : actorType === ESenderRole.PROVIDER
            ? "/assets/images/svg/stock_active_status.svg"
            : "/assets/images/svg/waiting_payment_status.svg",
        title:
          contract?.contractClassification === ContractClassification.BASIC ||
          contract?.contractClassification ===
            ContractClassification.BASIC_HARAJ
            ? t("global_status_payment_title")
            : actorType === ESenderRole.PROVIDER
            ? t("global_status_stock_title")
            : t("global_status_payment_title"),

        description:
          contract?.contractClassification === ContractClassification.BASIC ||
          contract?.contractClassification ===
            ContractClassification.BASIC_HARAJ
            ? actorType === ESenderRole.PROVIDER
              ? t("lbl_waiting_for_payment_description_seller")
              : t("status_payment_ubuyer_subtitle")
            : actorType === ESenderRole.PROVIDER
            ? t("status_stock_active_subtitle")
            : t("status_payment_ubuyer_subtitle"),
      },
      [ECONTRACT_STATUS.SUBMITTED]: {
        img: isContractWithoutReceiver
          ? "/assets/images/svg/profileYellow.svg"
          : "/assets/images/svg/waiting_payment_status.svg",
        title: isContractWithoutReceiver
          ? t("global_status_waiting_seller_title")
          : t("global_status_payment_title"),
        description: isContractWithoutReceiver
          ? actorType === ESenderRole.PROVIDER
            ? t("status_buyer_create_useller_waiting_seller_subtitle")
            : t("status_buyer_create_ubuyer_waiting_seller_subtitle")
          : actorType === ESenderRole.PROVIDER
          ? t("lbl_waiting_for_payment_description_seller")
          : t("status_payment_ubuyer_subtitle"),
      },
      [ECONTRACT_STATUS.PENDING_PAYMENT_VERIFICATION]: {
        img: "/assets/images/svg/waiting_payment_status.svg",
        title: t("global_status_payment_title"),
        description: t("lbl_pending_payment_made_via_bank"),
      },
      [ECONTRACT_STATUS.DRAFT]: {
        img: "/assets/images/svg/draft.svg",
        title: t("lbl_draft"),
        description: t("lbl_draft_status_subtitle"),
      },
      [ECONTRACT_STATUS.WAITING_FOR_DELIVERY]: {
        img: "/assets/images/svg/execution_status.svg",
        title: t("global_status_execution_title"),
        description:
          actorType === ESenderRole.PROVIDER
            ? t("status_execution_useller_subtitle")
            : t("status_execution_ubuyer_subtitle"),
      },
      [ECONTRACT_STATUS.PARTIAL_CONFIRMED_DELIVERY]: {
        img: "/assets/images/svg/execution_status.svg",
        title: t("global_status_execution_title"),
        description:
          actorType === ESenderRole.PROVIDER
            ? t("status_execution_useller_subtitle")
            : t("status_execution_ubuyer_subtitle"),
      },
      [ECONTRACT_STATUS.SHIPPED]: {
        img: "/assets/images/svg/execution_status.svg",
        title: t("global_status_shipped_title"),
        description: t("status_shipped_subtitle"),
      },
      [ECONTRACT_STATUS.ITEM_INSPECTION]: {
        img: "/assets/images/svg/inspection_status.svg",
        title: t("global_status_inspection_title"),
        description:
          actorType === ESenderRole.PROVIDER
            ? t("status_inspection_useller_subtitle")
            : t("status_inspection_ubuyer_subtitle"),
      },
      [ECONTRACT_STATUS.COMPLETED]: {
        img: contract?.escalations?.length
          ? "/assets/images/svg/closed_status.svg"
          : "/assets/images/svg/done_status.svg",
        title: contract?.escalations?.length
          ? t("global_status_closed")
          : t("global_status_finished"),
        description: t("global_status_thanks"),
      },
      [ECONTRACT_STATUS.PARTIALLY_RELEASED]: {
        img: contract?.escalations?.length
          ? "/assets/images/svg/closed_status.svg"
          : "/assets/images/svg/done_status.svg",
        title: contract?.escalations?.length
          ? t("global_status_closed")
          : t("global_status_finished"),
        description: t("global_status_thanks"),
      },
      [ECONTRACT_STATUS.EXCEPTIONAL_COMPLETED]: {
        img: contract?.escalations?.length
          ? "/assets/images/svg/closed_status.svg"
          : "/assets/images/svg/done_status.svg",
        title: contract?.escalations?.length
          ? t("global_status_closed")
          : t("global_status_finished"),
        description: t("global_status_thanks"),
      },
      [ECONTRACT_STATUS.DELIVERY_ESCALATION]: {
        img: "/assets/images/svg/escalation_status.svg",
        title: t("global_status_arbitration"),
        description:
          actorType === ESenderRole.PROVIDER
            ? t("status_arbitration_useller_subtitle")
            : t("status_arbitration_ubuyer_subtitle"),
      },
      [ECONTRACT_STATUS.INSPECTION_ESCALATION]: {
        img: "/assets/images/svg/escalation_status.svg",
        title: t("global_status_arbitration"),
        description:
          actorType === ESenderRole.PROVIDER
            ? t("status_arbitration_useller_subtitle")
            : t("status_arbitration_ubuyer_subtitle"),
      },
      [ECONTRACT_STATUS.RESOLVED_REFUND_CUSTOMER]: {
        img: "/assets/images/svg/closed_status.svg",
        title: t("global_status_closed"),
        description:
          contract?.customer?.id === Number(userId) ||
          contract?.customer?.id === Number(userIdentification)
            ? t("status_closed_ubuyer_for_seller_subtitle")
            : t("status_closed_useller_for_buyer_subtitle"),
      },
      [ECONTRACT_STATUS.RESOLVED_RELEASE_PROVIDER]: {
        img: "/assets/images/svg/closed_status.svg",
        title: t("global_status_closed"),
        description:
          contract?.customer?.id === Number(userId) ||
          contract?.customer?.id === Number(userIdentification)
            ? t("status_closed_ubuyer_for_buyer_subtitle")
            : t("status_closed_ubuyer_for_seller_subtitle"),
      },
      [ECONTRACT_STATUS.CLOSED_RELEASE_PROVIDER]: {
        img: "/assets/images/svg/closed_status.svg",
        title: t("global_status_closed"),
        description:
          contract?.customer?.id === Number(userId) ||
          contract?.customer?.id === Number(userIdentification)
            ? t("status_closed_ubuyer_for_buyer_subtitle")
            : t("status_closed_ubuyer_for_seller_subtitle"),
      },
      [ECONTRACT_STATUS.CLOSED_REFUND_CUSTOMER]: {
        img: "/assets/images/svg/closed_status.svg",
        title: t("global_status_closed"),
        description:
          contract?.customer?.id === Number(userId) ||
          contract?.customer?.id === Number(userIdentification)
            ? t("status_closed_ubuyer_for_seller_subtitle")
            : t("status_closed_useller_for_buyer_subtitle"),
      },
      [ECONTRACT_STATUS.CONTRACT_ACCEPTED]: {
        img: "/assets/images/svg/depositAmount.svg",
        title: t("lbl_deposit_the_amount"),
        description: t("lbl_waiting_for_amount"),
      },
      [ECONTRACT_STATUS.READY_FOR_PAYMENT]: {
        img: "/assets/images/svg/depositAmount.svg",
        title: t("lbl_deposit_the_amount"),
        description: t("lbl_waiting_for_amount"),
      },
      [ECONTRACT_STATUS.OUT_OF_STOCK]: {
        img:
          contract?.quantity === 0
            ? "/assets/images/svg/outofstock_status.svg"
            : pausedState && actorType === ESenderRole.PROVIDER
            ? "/assets/images/svg/paused_status.svg"
            : "/assets/images/svg/outofstock_status.svg",
        title:
          contract?.quantity === 0
            ? t("global_status_out_of_stock")
            : pausedState && actorType === ESenderRole.PROVIDER
            ? t("global_status_pause")
            : t("global_status_out_of_stock"),
        description:
          actorType === ESenderRole.PROVIDER
            ? pausedState
              ? t("status_stock_paused_subtitle")
              : t("lbl_modify_stock_quantity")
            : t("lbl_all_out_of_stock"),
      },
      [ECONTRACT_STATUS.PENDING_APPROVAL]: {
        img: "/assets/images/svg/approval.svg",
        title: t("lbl_waiting_for_approval_title"),
        description: t("lbl_waiting_for_approval_description"),
      },
      [ECONTRACT_STATUS.CONTRACT_REJECTED]: {
        img: "/assets/images/svg/errorStatus.svg",
        title: t("lbl_request_rejected_title"),
        description: t("lbl_request_rejected_description"),
      },
      [ECONTRACT_STATUS.ITEM_ACCEPTED]: {
        img: "/assets/images/svg/readyForCashout_status.svg",
        title:
          isMilestone || client_id === process.env.REACT_APP_HARAJ_CLIENT_ID
            ? t("haraj_status_settlement")
            : t("global_status_settlement"),
        description:
          isMilestone || client_id === process.env.REACT_APP_HARAJ_CLIENT_ID
            ? t("haraj_settlement_subtitle")
            : t("status_settlement_subtitle"),
      },
      [ECONTRACT_STATUS.READY_FOR_CASH_OUT]: {
        img: "/assets/images/svg/readyForCashout_status.svg",
        title: t("global_status_settlement"),
        description: t("status_settlement_subtitle"),
      },
      [ECONTRACT_STATUS.CASH_OUT_APPROVED]: {
        img: "/assets/images/svg/cashOutApproved_status.svg",
        title: t("global_status_transfer"),
        description: (
          <>
            {contract?.cashOutAmountList?.some(
              (item: CashOutAmount) => item.manualWithDrawCashOut === true
            ) && actorType !== ESenderRole.CUSTOMER
              ? t("status_transfer_manual_withdraw_subtitle")
              : isMilestone ||
                client_id === process.env.REACT_APP_HARAJ_CLIENT_ID
              ? t("status_haraj_transfer_subtitle")
              : t("status_transfer_subtitle")}{" "}
          </>
        ),
      },
    };
  }, [
    contract?.senderRole,
    contract?.receiverId,
    contract?.sourceContractId,
    contract?.contractClassification,
    contract?.escalations?.length,
    contract?.customer?.id,
    contract?.quantity,
    contract?.cashOutAmountList,
    actorType,
    t,
    userId,
    userIdentification,
    pausedState,
    isMilestone,
    client_id,
  ]);

  const subStatusConfig = useMemo(
    () => ({
      [ECONTRACT_STATUS.PENDING_PAYMENT_VERIFICATION]: {
        subImage: "/assets/images/svg/yellow_loader.svg",
        subTitle:
          actorType === ESenderRole.PROVIDER
            ? t("status_payment_useller_bank_transfer_hint_title")
            : t("status_payment_ubuyer_bank_transfer_hint_title"),
        subDescription:
          actorType === ESenderRole.PROVIDER
            ? t("status_payment_useller_bank_transfer_hint_subtitle")
            : t("status_payment_ubuyer_bank_transfer_hint_subtitle"),
        subBackground: "#FFC1070F",
      },
      [ECONTRACT_STATUS.WAITING_FOR_DELIVERY]: {
        subImage: "/assets/images/svg/shipping_address.gif",
        subTitle: isAddressAdded ? (
          <span className={classNames(styles["p1"], styles["g400"])}>
            {t("status_execution_ubuyer_view_address_hint_subtitle")}
          </span>
        ) : contract?.shippable &&
          Number(userIdentification) ===
            contract?.provider?.userIdentification ? (
          <>
            {t("status_pending_shipment_view_shipment_hint_subtitle")}
            <span
              className={classNames(styles["p1"])}
              style={{ fontWeight: "600" }}>
              {" " + `ناقل`}
            </span>
            {"، "}
            {t("status_shipped_ubuyer_view_shipment_hint_button")}
            <span
              className={classNames(styles["p1"])}
              style={{ fontWeight: "600" }}>
              {" " + contract?.shipment?.shipmentRequestId}
            </span>
          </>
        ) : (
          ""
        ),
        subDescription: "",
        subBackground: "#00897B0F",
      },
      [ECONTRACT_STATUS.SHIPPED]: {
        subImage: "/assets/images/svg/shipping_address.gif",
        subTitle: (
          <span className={classNames(styles["p1"], styles["g400"])}>
            {Number(userIdentification) ===
            contract?.customer?.userIdentification ? (
              <>
                {t("status_shipped_view_shipment_hint_subtitle")}
                <span
                  className={classNames(styles["p1"])}
                  style={{ fontWeight: "600" }}>
                  {" " + `ناقل`}
                </span>
              </>
            ) : (
              <>
                {t("status_shipped_view_shipment_hint_subtitle")}
                <span
                  className={classNames(styles["p1"])}
                  style={{ fontWeight: "600" }}>
                  {" " + `ناقل`}
                </span>
                {"، "}
                {t("status_shipped_ubuyer_view_shipment_hint_button")}
                <span
                  className={classNames(styles["p1"])}
                  style={{ fontWeight: "600" }}>
                  {" " + contract?.shipment?.shipmentRequestId}
                </span>
              </>
            )}
          </span>
        ),
        subDescription:
          Number(userIdentification) ===
          contract?.customer?.userIdentification ? (
            <span
              className={classNames(styles["greenLink"])}
              style={{ fontWeight: "700" }}>
              {`${t("status_shipped_ubuyer_view_shipment_hint_button")} ${
                contract?.shipment?.shipmentRequestId
              }`}
            </span>
          ) : (
            ""
          ),
        subBackground: "#00897B0F",
      },
      [ECONTRACT_STATUS.RESOLVED_RELEASE_PROVIDER]: {
        subImage:
          actorType === ESenderRole.CUSTOMER
            ? ""
            : "/assets/images/svg/yellow_loader.svg",
        subTitle:
          actorType === ESenderRole.CUSTOMER
            ? ""
            : t("global_status_waiting_transfer_hint_title"),
        subDescription:
          actorType === ESenderRole.CUSTOMER
            ? ""
            : t("global_status_waiting_transfer_hint_subtitle"),
        subBackground: "#9E9E9E0F",
      },
      [ECONTRACT_STATUS.RESOLVED_REFUND_CUSTOMER]: {
        subImage:
          contract?.customer?.id === Number(userId) ||
          contract?.customer?.id === Number(userIdentification)
            ? "/assets/images/svg/yellow_loader.svg"
            : "",
        subTitle:
          contract?.customer?.id === Number(userId) ||
          contract?.customer?.id === Number(userIdentification)
            ? t("global_status_waiting_transfer_hint_title")
            : "",
        subDescription:
          contract?.customer?.id === Number(userId) ||
          contract?.customer?.id === Number(userIdentification)
            ? t("global_status_waiting_transfer_hint_subtitle")
            : "",
        subBackground: "#9E9E9E0F",
      },
      [ECONTRACT_STATUS.CLOSED_RELEASE_PROVIDER]: {
        subImage:
          actorType === ESenderRole.CUSTOMER
            ? ""
            : "/assets/images/svg/completed_status.svg",
        subTitle:
          actorType === ESenderRole.CUSTOMER
            ? ""
            : t("global_status_transfer_done_hint_title"),
        subDescription:
          actorType === ESenderRole.CUSTOMER
            ? ""
            : t("global_status_transfer_done_hint_subtitle"),
        subBackground: "#9E9E9E0F",
      },
      [ECONTRACT_STATUS.CLOSED_REFUND_CUSTOMER]: {
        subImage:
          contract?.customer?.id === Number(userId) ||
          contract?.customer?.id === Number(userIdentification)
            ? "/assets/images/svg/completed_status.svg"
            : "",
        subTitle:
          contract?.customer?.id === Number(userId) ||
          contract?.customer?.id === Number(userIdentification)
            ? t("global_status_transfer_done_hint_title")
            : "",
        subDescription:
          contract?.customer?.id === Number(userId) ||
          contract?.customer?.id === Number(userIdentification)
            ? t("global_status_transfer_done_hint_subtitle")
            : "",
        subBackground: "#9E9E9E0F",
      },
    }),
    [
      t,
      isAddressAdded,
      actorType,
      contract?.shipment,
      userId,
      userIdentification,
      contract?.customer?.id,
      contract?.provider?.userIdentification,
      contract?.customer?.userIdentification,
      contract?.shippable,
    ]
  );

  if (!contract) return <></>;

  const { img, title, description } = statusConfig[contract?.status] || {};
  const { subImage, subTitle, subDescription, subBackground } =
    subStatusConfig[contract?.status] || {};

  return (
    <>
      {title && (
        <div className={styles["container"]}>
          <div className={styles["container_right"]}>
            <img src={img} />
            <div>
              <div
                style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <p className={styles["statusTitle"]}>{title}</p>
                {contract?.buyerOnly &&
                  contract.status === ECONTRACT_STATUS.CREATED && (
                    <TooltipWrapper
                      id="waffy-bank-info"
                      content={t("lbl_waffy_bank_info")}>
                      <button
                        onClick={() => setShowBankInfo(true)}
                        style={{
                          background: "none",
                          border: "none",
                          cursor: "pointer",
                          padding: 0,
                          display: "flex",
                          alignItems: "center",
                        }}>
                        <InfoIcon width={20} height={20} />
                      </button>
                    </TooltipWrapper>
                  )}
              </div>
              <p className={styles["p3"]}>{description}</p>
            </div>
          </div>
        </div>
      )}

      <WaffyBankInfoModal
        open={showBankInfo}
        onClose={() => setShowBankInfo(false)}
      />

      {subTitle && (
        <div
          className={styles["sub_container"]}
          style={{ backgroundColor: subBackground }}>
          <div className={styles["sub_container_right"]}>
            <img src={subImage} />
            <div>
              <p className={styles["stepperItem"]}>{subTitle}</p>
              <p className={styles["p3"]}>{subDescription}</p>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Status;
